import { ApiResponse } from 'apisauce';
import { camelizeKeys } from 'humps';

import { Api } from './api';
import { prepareOrder, mapOrder } from './orderKeyConversion';
import {
  getGeneralApiProblem,
  GetAllOrganizationsResult,
  DigitalOrdersBase,
  DigitalOrdersSaveResponse,
  GetSomeFacilitiesResult,
  GetAllStatesResult,
  GetAllCountriesResult,
  DigitalOrdersSendResponse,
} from '../models';

/**
 * Manages all requests to the Auth API.
 */
export class DigitalOrdersApi extends Api {
  async saveOrder(id: string, payload: DigitalOrdersBase): Promise<DigitalOrdersSaveResponse> {
    const converted = prepareOrder(payload);

    const response: ApiResponse<string> = camelizeKeys(
      await Api.apisauce.put(`/digital-orders/${id}`, converted),
    ) as ApiResponse<string>;

    if (!response.ok) {
      const problem = getGeneralApiProblem(response);
      if (problem) {
        return problem;
      }
    }

    try {
      if (response.data) {
        return { kind: 'ok', data: response.data };
      }
      return { kind: 'bad-data' };
    } catch {
      return { kind: 'bad-data' };
    }
  }

  async loadOrganizations(): Promise<any> {
    const response: ApiResponse<GetAllOrganizationsResult> = camelizeKeys(
      await Api.apisauce.get(`/organizations`),
    ) as ApiResponse<GetAllOrganizationsResult>;

    // the typical ways to die when calling an api
    if (!response.ok) {
      const problem = getGeneralApiProblem(response);
      if (problem) {
        return problem;
      }
    }

    // transform the data into the format we are expecting
    try {
      return { kind: 'ok', data: response.data };
    } catch {
      return { kind: 'bad-data' };
    }
  }

  async getOrganizationFacilities(id: string): Promise<any> {
    const response: ApiResponse<GetSomeFacilitiesResult> = camelizeKeys(
      await Api.apisauce.get(`/organizations/${id}/facilities`),
    ) as ApiResponse<GetSomeFacilitiesResult>;

    // the typical ways to die when calling an api
    if (!response.ok) {
      const problem = getGeneralApiProblem(response);
      if (problem) {
        return problem;
      }
    }

    // transform the data into the format we are expecting
    try {
      return { kind: 'ok', data: response.data };
    } catch {
      return { kind: 'bad-data' };
    }
  }

  async getFacilities(): Promise<any> {
    const response: ApiResponse<GetSomeFacilitiesResult> = camelizeKeys(
      await Api.apisauce.get('/facilities'),
    ) as ApiResponse<GetSomeFacilitiesResult>;

    if (!response.ok) {
      const problem = getGeneralApiProblem(response);
      if (problem) {
        return problem;
      }
    }

    try {
      return { kind: 'ok', data: response.data };
    } catch {
      return { kind: 'bad-data' };
    }
  }

  async loadCountries(): Promise<any> {
    const response: ApiResponse<GetAllCountriesResult> = camelizeKeys(
      await Api.apisauce.get(`/countries`),
    ) as ApiResponse<GetAllCountriesResult>;

    // the typical ways to die when calling an api
    if (!response.ok) {
      const problem = getGeneralApiProblem(response);
      if (problem) {
        return problem;
      }
    }

    // transform the data into the format we are expecting
    try {
      return { kind: 'ok', data: response.data };
    } catch {
      return { kind: 'bad-data' };
    }
  }

  async loadStates(): Promise<any> {
    const response: ApiResponse<GetAllStatesResult> = camelizeKeys(
      await Api.apisauce.get(`/states`),
    ) as ApiResponse<GetAllStatesResult>;

    // the typical ways to die when calling an api
    if (!response.ok) {
      const problem = getGeneralApiProblem(response);
      if (problem) {
        return problem;
      }
    }

    // transform the data into the format we are expecting
    try {
      return { kind: 'ok', data: response.data };
    } catch {
      return { kind: 'bad-data' };
    }
  }

  async loadDigitalOrders({
    userId,
    status,
    startDate,
    endDate,
  }: {
    userId?: string;
    status?: string[];
    startDate?: number;
    endDate?: number;
  }): Promise<any> {
    const params = Object.assign(
      {},
      userId && { userId },
      status && status.length && { status: status.join(',') },
      startDate && { startDate },
      endDate && { endDate },
    );

    let response: ApiResponse<any> = await Api.apisauce.get(`/digital-orders`, params);

    try {
      return { kind: 'ok', data: (response.data as DigitalOrdersBase[]).map(mapOrder) };
    } catch {
      return { kind: 'bad-data' };
    }
  }

  async removeDigitalOrderById(orderId: string): Promise<any> {
    const response: ApiResponse<string> = await Api.apisauce.delete(`/digital-orders/${orderId}`);

    // the typical ways to die when calling an api
    if (!response.ok) {
      const problem = getGeneralApiProblem(response);
      if (problem) {
        return problem;
      }
    }

    // transform the data into the format we are expecting
    try {
      return { kind: 'ok', data: response.data };
    } catch {
      return { kind: 'bad-data' };
    }
  }

  async sendEmail(id: string, payload: DigitalOrdersBase): Promise<DigitalOrdersSendResponse> {
    const converted = prepareOrder(payload);

    const response: ApiResponse<string> = camelizeKeys(
      await Api.apisauce.post(`/digital-orders/send/${id}`, converted),
    ) as ApiResponse<string>;

    if (!response.ok) {
      const problem = getGeneralApiProblem(response);
      if (problem) {
        return problem;
      }
    }

    return { kind: 'ok' };
  }
}
